<template>
  <label
    class="textfield-label w-full"
    :class="labelClass"
    :style="labelStyle"
    :data-cy="dataCy"
  >
    <div class="d-flex align-items-center">
      <slot name="label">
        <span v-if="labelText && !noLabel" class="label">{{ labelText }}</span>
        <span v-if="hintLabel" class="hint-label">{{ hintLabel }}</span>
        <span v-if="required" class="textfield-required text-danger">*</span>
        <span v-if="tooltipText !== ''">
          <Tooltip :placement="tooltipPlacement">
            <template #trigger>
              <i class="fas fa-info-circle ml-1 mb-2"></i>
            </template>
            <template #tooltip>
              <span>{{ tooltipText }}</span>
            </template>
          </Tooltip>
        </span>
      </slot>
      <div class="right-label">
        <slot name="right-label"></slot>
      </div>
    </div>
    <div class="d-flex flex-column full-width">
      <div :class="['w-full', { 'textfield-with-icon': hasIcon }]">
        <input
          v-if="currency"
          ref="input"
          v-money3="money"
          :value="inputVal"
          :class="[
            'form-control',
            inputClass,
            { 'is-invalid': hasError, 'success-input': success }
          ]"
          :placeholder="placeholder || labelText"
          v-bind="$attrs"
          :data-cy="testId"
          @input="inputChange"
          @blur="$emit('blur')"
          @focus="preventCursorMove($event)"
        />
        <input
          v-else
          ref="input"
          :value="inputVal"
          :class="[
            'form-control',
            inputClass,
            { 'is-invalid': hasError, 'success-input': success }
          ]"
          :placeholder="placeholder || labelText"
          v-bind="$attrs"
          :data-cy="testId"
          :type="search ? 'search' : type"
          @input="inputChange"
          @focus="$emit('focus', $event)"
          @blur="$emit('blur', $event)"
          @keypress="$emit('keypress', $event)"
          @keyup.enter="$emit('enter', $event)"
        />
        <i
          v-if="(search && inputVal) || clearable"
          class="fa-light fa-xmark cursor-pointer textfield-icon clear-icon"
          @click="clearValue"
        ></i>
        <i
          v-else-if="search && !inputVal"
          class="fa-light fa-magnifying-glass textfield-icon"
        ></i>
        <i
          v-if="success"
          class="fa-regular fa-circle-check success-icon textfield-icon"
        ></i>
      </div>
      <span v-if="errorMessageDisplay" class="text-danger error-text">{{
        errorMessageDisplay
      }}</span>
    </div>
  </label>
</template>

<script>
import { Money3Directive } from "v-money3";
import { nextTick } from "vue";
import { kebabCase } from "@/lib/helpers.js";
import { findByName } from "@/lib/vuelidate_helpers";

import { formatPercent } from "@/mixins";
export default {
  directives: { money3: Money3Directive },
  inheritAttrs: false,
  props: {
    autoFocus: {
      type: Boolean,
      default: false
    },
    clearable: { type: Boolean, default: false },
    tooltipPlacement: { type: String, default: "top" },
    tooltipText: { type: String, default: "" },
    modelValue: { type: [Number, String], default: "" },
    name: { type: String, default: "" },
    currency: { type: Boolean, default: false },
    placeholder: { type: String, default: "" },
    label: { type: String, default: "" },
    labelStyle: { type: String, default: "" },
    labelClass: { type: String, default: "" },
    inputClass: { type: String, default: "" },
    hintLabel: { type: String, default: "" },
    noLabel: {
      type: Boolean,
      default: false
    },
    validationErrors: {
      type: Object,
      default: null
    },
    required: {
      type: Boolean,
      default: () => false
    },
    error: {
      type: Boolean,
      default: () => false
    },
    errorMessage: {
      type: String,
      default: () => ""
    },
    percent: {
      type: Boolean,
      default: false
    },
    type: {
      type: String,
      default: ""
    },
    dataCy: {
      type: String,
      default: ""
    },
    search: {
      type: Boolean,
      default: false
    },
    success: {
      type: Boolean,
      default: false
    }
  },
  emits: ["update:modelValue", "blur", "focus", "keypress", "enter"],
  data() {
    return {
      money: {
        decimal: ".",
        thousands: ",",
        prefix: "$",
        suffix: "",
        precision: 2,
        masked: false
      }
    };
  },
  computed: {
    hasError() {
      return this.errorMessageDisplay || this.error;
    },
    errorMessageDisplay() {
      if (this.errorMessage !== "") return this.errorMessage;
      if (!this.validationErrors || !this.name) return undefined;
      const errorFound = findByName(this.validationErrors, this.name);
      if (errorFound) {
        return errorFound;
      }
      return undefined;
    },
    inputVal: {
      get() {
        // Fix currency input cursor position on change
        // Prevents v-money directive from moving cursor to end of input
        if (this.currency) {
          return this.formatCurrency(this.modelValue);
        }
        if (this.percent) {
          return this.formatPercent(this.modelValue);
        }
        return this.modelValue;
      }
    },
    labelText() {
      return this.label || this.name;
    },
    testId() {
      return kebabCase(this.labelText) || "text-field";
    },
    hasIcon() {
      return this.search || this.success || this.clearable;
    }
  },
  mounted() {
    if (this.autoFocus && this.$refs.input) {
      nextTick(() => {
        this.$refs.input.focus();
      });
    }
  },
  methods: {
    inputChange(event) {
      const value = event.target.value;
      let tempValue = value;
      if (this.currency) {
        tempValue = this.parseCents(value);
      }
      if (this.percent) {
        tempValue = this.parsePercent(value);
      }
      return this.$emit("update:modelValue", tempValue);
    },
    formatCurrency(cents) {
      return (cents / 100).toLocaleString("en-us", {
        style: "currency",
        currency: "USD"
      });
    },
    parseCents(dollarString) {
      const parsed = dollarString.replace(/[$,.]/g, "");
      return parseFloat(Number(parsed).toFixed(2));
    },
    parsePercent(pctString) {
      // Better handle backspace in input
      if (pctString.indexOf("%") < 0) {
        pctString = pctString.substring(0, pctString.length - 1);
      }
      const pct = parseFloat(pctString.replace(/%/, ""));
      return !isNaN(pct) ? pct / 100 : 0;
    },
    formatPercent(percent) {
      return formatPercent(percent);
    },
    preventCursorMove(event) {
      // Fix currency input cursor position on focus
      // Prevents v-money directive from moving cursor to end of input
      event.stopImmediatePropagation();
      this.$emit("focus", event);
    },
    clearValue() {
      this.$emit("update:modelValue", "");
    }
  }
};
</script>

<style lang="scss" scoped>
.right-label {
  margin-left: auto;
}
.hint-label {
  color: #626262;
  font-style: italic;
}
.textfield-label {
  margin-bottom: 0.5rem;
  color: var(--font-color);
}

.form-control {
  color: var(--font-color);
}

.label,
.hint-label {
  margin-left: 2px;
  margin-bottom: 3px;
  display: inline-block;
}
/* Chrome, Safari, Edge, Opera */
input::-webkit-outer-spin-button,
input::-webkit-inner-spin-button {
  -webkit-appearance: none;
  margin: 0;
}

/* Firefox */
input[type="number"] {
  -moz-appearance: textfield;
}
.textfield-with-icon {
  position: relative;
  input {
    padding-right: 20px !important;
  }
  .textfield-icon {
    position: absolute;
    top: 45%;
    right: 5px;
    transform: translateY(-50%);
    height: 0.5em;
    padding: 5px;
    box-sizing: content-box;
  }
}
.success-input {
  border-color: var(--field-success);
}
.success-icon {
  color: var(--field-success);
}

input.ss-input-group-prepend {
  border-bottom-right-radius: 0;
  border-top-right-radius: 0;
}
.textfield-required {
  margin-left: 3px;
  align-self: baseline;
}
</style>
